import { useEffect } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { TradingHistoryViewedProperties } from '@iwoca/frontend-tracking-library/lib/ampli';

import DocumentRequirementStep from './DocumentRequirementStep/DocumentRequirementStep';
import { getLoanSummary } from './helpers/loanSummary.helper';
import LoanSummaryRevamp from './LoanSummaryRevamp';
import AgedDebtorReport from './RequirementTypes/AgedDebtorReport';
import BankStatements from './RequirementTypes/BankStatements';
import BusinessPlan from './RequirementTypes/BusinessPlan';
import CompanyAccounts from './RequirementTypes/CompanyAccounts';
import ManagementAccounts from './RequirementTypes/ManagementAccounts';
import VATReturns from './RequirementTypes/VATReturns';
import { StepProvider } from './StepContext';
import styles from './TradingHistoryRevamp.module.css';
import { useTradingHistorySteps } from './useTradingHistorySteps';
import {
  useGetApplicationQuery,
  useGetDocuments,
} from '../../../api/lendingApi/lendingApiQueries';
import { useContextStateKey } from '../../../hooks/useContextStateKey';
import { useNumberFormat } from '../../../hooks/useNumberFormat';
import { useOffersUK } from '../../../hooks/useOffers/useOffersUK';
import { getAmplitudeDefaultEventProperties } from '../../../utils/amplitudeEvents';
import { Button } from '../../Button/Button';
import { ProgressBar } from '../ProgressBar/ProgressBar';
import { TradingHistoryLoader } from '../TradingHistoryLoader';
import { LoanSummaryLoader } from '../TradingHistoryUK/LoanSummaryLoader';
import { amplitudeDocRequirementStatusEventProperties } from '../TradingHistoryUK/TradingHistoryUK.tracking.helper';

const DOCUMENT_REQUIREMENTS: Record<string, React.ElementType> = {
  aged_debtor_report: AgedDebtorReport,
  bank_statement: BankStatements,
  business_plan: BusinessPlan,
  company_accounts: CompanyAccounts,
  management_accounts: ManagementAccounts,
  vat_return: VATReturns,
};

const TradingHistoryRevamp = () => {
  const { formatCurrency } = useNumberFormat();

  const { application, isApplicationLoading } = useGetApplicationQuery();
  const stateKey = useContextStateKey();
  const { documents, isLoading, isRefetching, refetch } =
    useGetDocuments(stateKey);

  const {
    documentRequirements,
    isLoadingOffers,
    offers,
    handleDecision,
    isDeciding,
    isRefetchingOffers,
  } = useOffersUK({
    enabled: true,
  });

  const requiredDocumentRequirements =
    documentRequirements?.filter((doc) => !doc.is_optional) || [];

  const { displayAmount, maxTermLength, showRequestedAmount } = getLoanSummary(
    application,
    offers,
  );

  const { steps, showSummary, onSubmit, isSubmitting } = useTradingHistorySteps(
    {
      requirements: requiredDocumentRequirements,
      documents,
      refetch,
      stateKey,
      onDecision: handleDecision,
      isDeciding,
      isRefetchingOffers,
      isReady:
        !!documents?.data && !isLoading && !isRefetching && !isLoadingOffers,
    },
  );

  useEffect(() => {
    tracking.ampli.stepLoaded({
      'Step name': 'Trading history',
      ...getAmplitudeDefaultEventProperties(),
    });
  }, []);

  useEffect(() => {
    const suggestedOffer = offers?.find((offer) => offer.is_suggested);
    const requestedProduct = application?.requested_products?.[0];

    if (!requestedProduct || !documentRequirements || !suggestedOffer) {
      return;
    }

    const eventProperties: Pick<
      TradingHistoryViewedProperties,
      | 'requested_amount'
      | 'loan_probability'
      | 'suggested_amount'
      | 'Page version'
      | 'Requested product type'
    > = {
      // @ts-expect-error it can be null in the schema
      requested_amount: requestedProduct.amount,
      loan_probability: suggestedOffer.probability_of_offer,
      suggested_amount: requestedProduct.suggested_product?.amount,
      'Page version': 'v3',
      'Requested product type': requestedProduct.product_type || '',
    };

    tracking.ampli.tradingHistoryViewed({
      ...eventProperties,
      ...amplitudeDocRequirementStatusEventProperties(documentRequirements),
    });
  }, [Boolean(documentRequirements)]);

  if (isLoadingOffers || isLoading || isApplicationLoading) {
    return (
      <div className={styles.container}>
        <ProgressBar />
        <div className={styles.content}>
          <LoanSummaryLoader />
          <TradingHistoryLoader />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <ProgressBar />
      <div className={styles.content}>
        <LoanSummaryRevamp
          displayAmount={displayAmount}
          maxTermLength={maxTermLength}
          showRequestedAmount={showRequestedAmount}
        />
        <h1 className={styles.heading}>
          You’re eligible for up to{' '}
          <span className={styles.amount}>
            {formatCurrency(displayAmount)}*
          </span>
        </h1>
        <p className={styles.subheading}>
          We just need a little bit more information to be able to make the most
          accurate assessment.
        </p>
        {steps.length > 0 && (
          <div className={styles.steps}>
            {steps.map((step) => {
              const RequirementContent =
                DOCUMENT_REQUIREMENTS[step.requirement.type];
              if (!RequirementContent) return null;
              if (!step.title) return null;

              return (
                <StepProvider key={step.requirement.type} value={step}>
                  <DocumentRequirementStep>
                    <RequirementContent />
                  </DocumentRequirementStep>
                </StepProvider>
              );
            })}
          </div>
        )}
        {showSummary && (
          <div className={styles.submitSection}>
            <Button
              className={styles.submitButton}
              loading={isSubmitting}
              onClick={onSubmit}
            >
              Submit application
            </Button>
            <p className={styles.submitText}>
              This will submit your application for review
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TradingHistoryRevamp;
